import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Save, Eye, Globe, Upload, Plus, Trash2,
  GripVertical, Image, Video, Music, Calendar, Mail,
  User, Layout, Palette, Settings, ExternalLink, Newspaper, Copy, Quote, Star,
  Briefcase, Users, HelpCircle, MessageSquare, Phone, CheckCircle2, XCircle, AlertTriangle, Link, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import CloudinaryUploader, { GalleryUploader } from '@/components/CloudinaryUploader';

export const TeamModuleEditor = ({ data, onChange, siteSlug }) => {
  const members = data.members || [];
  const layout = data.layout || 'grid';
  const [editingIdx, setEditingIdx] = useState(null);
  
  const addMember = () => {
    const newMember = { id: Date.now().toString(), name: 'New Team Member', role: '', bio: '', image: '', social: {} };
    onChange({...data, members: [...members, newMember]});
    setEditingIdx(members.length);
  };
  
  const updateMember = (idx, updates) => {
    const updated = [...members];
    updated[idx] = {...updated[idx], ...updates};
    onChange({...data, members: updated});
  };
  
  const removeMember = (idx) => {
    onChange({...data, members: members.filter((_, i) => i !== idx)});
    setEditingIdx(null);
  };

  const renderEditor = (item, idx) => (
    <div className="bg-slate-800 border border-slate-600 rounded-lg p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-white">Edit Team Member</h4>
        <Button variant="ghost" size="sm" onClick={() => setEditingIdx(null)} className="text-slate-400 hover:text-white">Done</Button>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Name</Label>
          <Input value={item.name} onChange={(e) => updateMember(idx, { name: e.target.value })} placeholder="Full name" />
        </div>
        <div className="space-y-2">
          <Label>Role/Title</Label>
          <Input value={item.role || ''} onChange={(e) => updateMember(idx, { role: e.target.value })} placeholder="e.g. Musical Director" />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Bio</Label>
        <Textarea value={item.bio || ''} onChange={(e) => updateMember(idx, { bio: e.target.value })} placeholder="Short bio..." rows={3} />
      </div>
      
      <div className="space-y-2">
        <Label>Photo</Label>
        <CloudinaryUploader
          currentImage={item.image}
          onUpload={(url) => updateMember(idx, { image: url })}
          onRemove={() => updateMember(idx, { image: '' })}
          folder={`sites/${siteSlug}/team`}
          aspectRatio="aspect-square"
          placeholder="Drop team photo"
        />
      </div>

      <div className="pt-2 border-t border-slate-700">
        <Button variant="ghost" size="sm" onClick={() => removeMember(idx)} className="text-red-400 hover:text-red-300 hover:bg-red-500/20">
          <Trash2 className="w-4 h-4 mr-2" /> Remove Member
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Section Title</Label>
        <Input value={data.title || 'The Team'} onChange={(e) => onChange({...data, title: e.target.value})} />
      </div>

      <div className="space-y-3">
        <Label>Display Layout</Label>
        <div className="flex items-center gap-2">
          {['grid', 'list', 'carousel'].map(l => (
            <button key={l} onClick={() => onChange({...data, layout: l})} className={cn("flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all capitalize", layout === l ? "bg-orange-500/20 border-orange-500 text-orange-400" : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500")}>
              <span className="text-xs">{l}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Team Members ({members.length})</Label>
          <Button variant="outline" size="sm" onClick={addMember}><Plus className="w-4 h-4 mr-1" /> Add Member</Button>
        </div>
        
        {members.length === 0 ? (
          <div className="text-center py-12 bg-slate-700/30 rounded-lg border border-slate-600">
            <Users className="w-10 h-10 mx-auto text-slate-500 mb-3" />
            <p className="text-sm text-slate-400">No team members yet. Add your collaborators!</p>
          </div>
        ) : editingIdx !== null && members[editingIdx] ? (
          renderEditor(members[editingIdx], editingIdx)
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {members.map((item, idx) => (
              <div key={item.id || idx} onClick={() => setEditingIdx(idx)} className="flex items-center gap-3 p-3 bg-slate-700/30 border border-slate-600 rounded-lg cursor-pointer hover:bg-slate-700/50 hover:border-slate-500 transition-all group">
                <div className="w-12 h-12 rounded-full bg-slate-600 overflow-hidden flex-shrink-0">
                  {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><User className="w-5 h-5 text-slate-500" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-white truncate text-sm">{item.name || 'Untitled'}</h4>
                  <p className="text-xs text-slate-400 truncate">{item.role || 'No role'}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeMember(idx); }} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// FAQ Module Editor

